import React, { useState, useEffect, useRef } from 'react';
import Card from '../components/common/Card';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const timerRef = useRef(null);

  const phrases = [
    'Questions?',
    'Answers!',
    'Help?'
  ];

  useEffect(() => {
    const handleType = () => {
      const currentPhrase = phrases[loopNum % phrases.length];
      
      if (!isDeleting) {
        setDisplayText(currentPhrase.substring(0, displayText.length + 1));
        
        if (displayText.length === currentPhrase.length) {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        setDisplayText(currentPhrase.substring(0, displayText.length - 1));
        
        if (displayText.length === 0) {
          setIsDeleting(false);
          setLoopNum(loopNum + 1);
        }
      }
    };

    const typeSpeed = isDeleting ? 50 : 100;
    timerRef.current = setTimeout(handleType, typeSpeed);

    return () => clearTimeout(timerRef.current);
  }, [displayText, isDeleting, loopNum, phrases]);

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: 'How do I create an account?',
          answer: 'To create an account, click on the "Register" button in the top right corner of the homepage. Fill in your name, email address, and create a password. You can also register using your Google or Facebook account for quicker sign-up.'
        },
        {
          question: 'Is it free to join SALON?',
          answer: 'Yes, creating an account on SALON is completely free. You can browse services, view provider profiles, and compare options without any charges. You only pay when you book and pay for a service.'
        },
        {
          question: 'How do I find service providers near me?',
          answer: 'You can use our search feature to find providers by location, service category, or specific keywords. Simply enter your location and the type of service you are looking for, and we will show you available providers in your area.'
        }
      ]
    },
    {
      category: 'Booking & Appointments',
      questions: [
        {
          question: 'How do I book an appointment?',
          answer: 'To book an appointment, first find a service provider that meets your needs. View their profile to see available services and pricing. Select the service you want, choose an available date and time, and complete the payment to confirm your booking.'
        },
        {
          question: 'Can I modify or cancel my booking?',
          answer: 'Yes, you can modify or cancel your booking through your "My Bookings" page. Please note that cancellation policies vary by provider. Some providers offer full refunds for cancellations made 24+ hours before the appointment, while others may have different policies.'
        },
        {
          question: 'What happens if I miss my appointment?',
          answer: 'If you miss your appointment without prior notice, the provider may charge a no-show fee. We recommend contacting the provider as soon as possible if you need to reschedule.'
        },
        {
          question: 'How far in advance can I book?',
          answer: 'Booking availability varies by provider. Most providers allow bookings from the next day up to 30-60 days in advance. You can see available time slots when selecting your preferred provider.'
        }
      ]
    },
    {
      category: 'Payments & Refunds',
      questions: [
        {
          question: 'What payment methods are accepted?',
          answer: 'We accept major credit and debit cards (Visa, MasterCard, American Express), PayPal, and other payment methods as indicated on our platform. All payments are processed securely through our payment partners.'
        },
        {
          question: 'When am I charged for a booking?',
          answer: 'You are charged at the time of booking to confirm your appointment. The full amount is held securely and released to the provider after your service is completed.'
        },
        {
          question: 'How do refunds work?',
          answer: 'Refunds are processed according to the provider is cancellation policy. If a provider cancels your appointment, you will receive a full refund automatically. If you cancel within the allowed time frame, your refund will be processed within 5-7 business days.'
        }
      ]
    },
    {
      category: 'For Service Providers',
      questions: [
        {
          question: 'How do I become a service provider on SALON?',
          answer: 'To become a service provider, click on "Become a Provider" and complete the registration process. You will need to provide your business information, service offerings, pricing, and verify your identity. Our team will review your application.'
        },
        {
          question: 'What fees do providers pay?',
          answer: 'SALON charges a small commission fee on each completed booking. The exact fee structure is provided in the provider agreement. There are no upfront fees to list your services.'
        },
        {
          question: 'How do I manage my availability?',
          answer: 'As a provider, you can manage your availability through your dashboard. Set your working hours, block off specific dates, and customize your booking settings to control when clients can book appointments.'
        }
      ]
    },
    {
      category: 'Account & Security',
      questions: [
        {
          question: 'How do I reset my password?',
          answer: 'Click on "Login" and then select "Forgot Password." Enter your email address, and we will send you a link to reset your password. The link expires after 24 hours for security purposes.'
        },
        {
          question: 'How is my personal information protected?',
          answer: 'We take your privacy seriously. Your personal information is encrypted and stored securely. We never share your data with third parties without your consent. Please refer to our Privacy Policy for more details.'
        },
        {
          question: 'How can I delete my account?',
          answer: 'To delete your account, go to your Profile settings and select "Delete Account." Please note that this action is permanent and cannot be undone. All your data will be removed from our system within 30 days.'
        }
      ]
    },
    {
      category: 'Support',
      questions: [
        {
          question: 'How can I contact customer support?',
          answer: 'You can reach our customer support team through the Contact Us page, by email at support@helloparlour.com, or by calling +256 123 456789. Our team is available Monday through Saturday, 8 AM to 8 PM.'
        },
        {
          question: 'What should I do if I have a complaint about a service?',
          answer: 'If you have a complaint about a service, please first try to resolve it directly with the provider. If you cannot reach a resolution, contact our support team within 48 hours of your appointment, and we will investigate the matter.'
        }
      ]
    }
  ];

  const handleToggle = (categoryIndex, questionIndex) => {
    const globalIndex = faqs.slice(0, categoryIndex).reduce((acc, cat) => acc + cat.questions.length, 0) + questionIndex;
    setOpenIndex(openIndex === globalIndex ? -1 : globalIndex);
  };

  const getGlobalIndex = (categoryIndex, questionIndex) => {
    return faqs.slice(0, categoryIndex).reduce((acc, cat) => acc + cat.questions.length, 0) + questionIndex;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="py-12 px-4 mt-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Got{' '}
            <span className="text-orange-500">
              {displayText}
              <span className="inline-block w-1 h-8 md:h-10 bg-orange-500 ml-1 animate-pulse"></span>
            </span>
          </h1>
          <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto">
            Find answers to common questions about our platform.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4 space-y-8 rounded-2xl border border-orange-200 p-8">
        {/* Search */}
        <section className="max-w-xl mx-auto">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for answers..."
              className="w-full pl-4 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <svg className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </section>

        {/* FAQ Categories */}
        {faqs.map((category, categoryIndex) => (
          <section key={categoryIndex}>
            <h2 className="text-xl font-bold text-gray-800 mb-4">{category.category}</h2>
            <div className="space-y-3">
              {category.questions.map((faq, questionIndex) => {
                const globalIndex = getGlobalIndex(categoryIndex, questionIndex);
                const isOpen = openIndex === globalIndex;
                
                return (
                  <div key={questionIndex} className="bg-white rounded-xl border border-orange-100 overflow-hidden">
                    <button
                      onClick={() => handleToggle(categoryIndex, questionIndex)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-orange-50 transition-colors"
                    >
                      <span className="font-medium text-gray-800 pr-4">{faq.question}</span>
                      {isOpen ? (
                        <ChevronUpIcon className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {/* Contact CTA */}
        <section className="bg-orange-50 rounded-xl p-8 text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Still have questions?</h2>
          <p className="text-gray-600 mb-4">Could not find the answer you are looking for? Please contact our support team.</p>
          <a href="/contact" className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors">Contact Us</a>
        </section>
      </div>
    </div>
  );
};

export default FAQ;
